import React from "react";
import { StyledButton } from "../../styledComponents/buttons";

import planActive from "../../assets/img/subscription-status-active.svg";
import CardContainer from "./CardContainer";
import { CapitalizedH3 } from "../../styledComponents/common";
import { getCurrencySymbol, getPlanName, getPlanType, reformatDate } from "../../utils/helpers";
import { getIntegerPriceLabel } from "./billingCatalog";

// You have insufficient funds in your account
// Your payment card has expired
// There's a problem with your bank

const possibleReasons = [
  {
    color: "#FFC107",
    text: "You have insufficient funds in your account",
  },
  {
    color: "#FFC107",
    text: "Your payment card has expired",
  },
  {
    color: "#FFC107",
    text: "There's a problem with your bank",
  },
];

const getNextBillingDate = (planInfo) =>
  planInfo?.is_billing
    ? planInfo?.payment_information?.next_billed_at || planInfo?.next_billed_at
    : planInfo?.payment_information?.next_payment?.date ||
      planInfo?.next_billed_at;

function getScheduledChangeCopy(scheduledChange) {
  if (!scheduledChange?.action || !scheduledChange?.effective_at) {
    return "";
  }

  if (scheduledChange.action === "pause") {
    return `Your subscription is scheduled to pause on ${reformatDate(
      scheduledChange.effective_at
    )}.`;
  }

  if (scheduledChange.action === "cancel") {
    return `Your subscription is scheduled to cancel on ${reformatDate(
      scheduledChange.effective_at
    )}.`;
  }

  if (scheduledChange.action === "resume") {
    return `Your subscription is scheduled to resume on ${reformatDate(
      scheduledChange.effective_at
    )}.`;
  }

  return "";
}

const CurrentPlan = ({
  modal,
  setModal,
  planInfo,
  resumeSubscription,
  isBilling,
  actions,
  openBillingPortal,
  billingPortalSessionLoading,
}) => {
  const nextBillingDate = getNextBillingDate(planInfo);
  const displayPlanId =
    planInfo?.payment_information?.price_id ||
    planInfo?.price_id ||
    planInfo?.paddle_plan_id;
  const backendPlanName = planInfo?.plan_name;
  const displayPlanName = backendPlanName || getPlanName(displayPlanId);
  const displayPlanType = backendPlanName
    ? /monthly/i.test(backendPlanName)
      ? " / month"
      : /yearly/i.test(backendPlanName)
      ? " / year"
      : ""
    : getPlanType(displayPlanId);
  const displayPrice =
    planInfo?.unit_price != null && planInfo?.unit_price !== ""
      ? getCurrencySymbol(planInfo?.currency) +
        planInfo.unit_price +
        displayPlanType
      : "Price unavailable";
  const integerDisplayPrice = getIntegerPriceLabel(displayPrice);
  const subscriptionActions = actions || {};
  const scheduledChangeCopy = getScheduledChangeCopy(planInfo?.scheduled_change);

  const handleManageSubscriptionClick = (event) => {
    event.preventDefault();

    openBillingPortal?.(planInfo);
  };

  return (
    <div className="subscontent">
      <div
        style={{ padding: "2.5rem 2.5rem 0rem 2.5rem" }}
        className="innerTop"
      >
        <p className="subheader">Current Plan</p>
        {scheduledChangeCopy && (
          <div
            style={{
              marginBottom: "20px",
              padding: "14px 16px",
              borderRadius: "12px",
              backgroundColor: "#eef4ff",
              color: "#1d4ed8",
              fontSize: "14px",
              lineHeight: "1.5",
            }}
          >
            {scheduledChangeCopy}
          </div>
        )}
        <div className="subcenter">
          <div className="leftsubcontent">
            <div className="subs-rate-container">
              <CapitalizedH3>
                {displayPlanName === "Unknown" ? "Unknown" : displayPlanName}
              </CapitalizedH3>
              <span className="subs-rate">{integerDisplayPrice}</span>
            </div>
            <div
              style={{
                margin: "20px 0",
                fontSize: "16px",
              }}
            >
              <p
                style={{
                  color: "#52575f",
                }}
              >
                Status
              </p>
              <span>
                <img
                  style={{
                    height: "20px",
                    width: "20px",
                    marginRight: "10px",
                  }}
                  src={planActive}
                  alt="plan active"
                  width="100%"
                />
                {planInfo?.status === "trialing"
                  ? "Trial"
                  : planInfo?.status === "active"
                  ? "Active"
                  : planInfo?.status === "past_due"
                  ? "Past Due"
                  : "Active"}
              </span>
              <span
                style={{
                  display: planInfo?.status === "past_due" ? "block" : "none",
                  marginTop: "20px",
                }}
              >
                <p style={{ 
                  color: "#52575f",
                  fontSize: "15px",
                  marginBottom: "5px"
                   }}>
                  We can see that your subscription has gone past due. It could
                  be because-
                </p>
                {possibleReasons.map((reason, index) => (
                  <span
                    className="possible-reasons"
                    key={index}
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "center",
                      fontSize: "14px",
                    }}
                  >
                    <div
                      style={{
                        height: "10px",
                        width: "10px",
                        backgroundColor: reason.color,
                        borderRadius: "50%",
                      }}
                    />
                    <span>{reason.text}</span>
                  </span>
                ))}
              </span>
            </div>
            <div>
              <p style={{ color: "#52575f" }}>
                Member Since{" "}
                <span style={{ color: "#000" }}>
                  {reformatDate(planInfo?.created_at)}
                </span>{" "}
              </p>
              {!planInfo?.payment_information ? (
                <p>{""}</p>
              ) : nextBillingDate &&
                (planInfo?.payment_information?.paused_from === null ||
                  planInfo?.payment_information?.paused_from === undefined) ? (
                <p style={{ color: "#52575f" }}>
                  Next Billing Period{" "}
                  <span style={{ color: "#000" }}>
                    {reformatDate(nextBillingDate)}
                  </span>{" "}
                </p>
              ) : (
                planInfo?.payment_information?.paused_at && (
                  <p style={{ color: "#52575f" }}>
                    Subscription paused at{" "}
                    <span style={{ color: "#000" }}>
                      {" "}
                      {reformatDate(
                        planInfo?.payment_information?.paused_at
                      )}{" "}
                    </span>
                    effective from{" "}
                    {planInfo?.payment_information?.paused_from !==
                      "waiting" && (
                      <span
                        style={{
                          color: "#000",
                        }}
                      >
                        {reformatDate(
                          planInfo?.payment_information?.paused_from
                        )}
                        {subscriptionActions.can_resume !== false && (
                          <button
                            onClick={() => {
                              if (isBilling) {
                                openBillingPortal?.(planInfo);
                                return;
                              }

                              resumeSubscription({ pause: false });
                            }}
                            className="resume-subscription-btn"
                          >
                            {isBilling ? "Manage in Portal" : "Resume Subscription"}
                          </button>
                        )}
                      </span>
                    )}
                  </p>
                )
              )}
            </div>
          </div>
          <div className="rightsubcontent">
            <StyledButton
              onClick={() => {
                setModal({
                  upgradeModal: !modal.upgradeModal,
                });
              }}
              disabled={subscriptionActions.can_change_plan === false}
              backgroundColor="#33CDD1"
              textColor="#fff"
              fontSize="16px"
              fontWeight="600"
              width="180px"
              hoverBackgroundColor="#3ab7bb"
            >
              {isBilling ? "Change Plan" : "Upgrade Plan"}
            </StyledButton>
            {isBilling && (
              <button
                type="button"
                onClick={handleManageSubscriptionClick}
                disabled={billingPortalSessionLoading}
                style={{
                  width: "180px",
                  textAlign: "center",
                  color:
                    !billingPortalSessionLoading
                      ? "#007bff"
                      : "#98a2b3",
                  fontSize: "16px",
                  fontWeight: "600",
                  textDecoration: "none",
                  marginTop: "4px",
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor:
                    !billingPortalSessionLoading
                      ? "pointer"
                      : "not-allowed",
                }}
              >
                {billingPortalSessionLoading
                  ? "Opening portal..."
                  : "Manage Subscription"}
              </button>
            )}
            {!isBilling && (
              <StyledButton
                onClick={() => {
                  setModal({
                    cancelModal: !modal.cancelModal,
                  });
                }}
                disabled={subscriptionActions.can_cancel === false}
                backgroundColor="#fff"
                textColor="#52575F"
                fontSize="16px"
                fontWeight="600"
                width="180px"
                border="1px solid #dfe5f3"
                hoverBorder="1px solid #C2CCE1"
              >
                Cancel Subscription
              </StyledButton>
            )}
            {subscriptionActions.blocked_reason && (
              <p
                style={{
                  marginTop: "12px",
                  maxWidth: "220px",
                  fontSize: "13px",
                  lineHeight: "1.5",
                  color: "#7a5b00",
                }}
              >
                {subscriptionActions.blocked_reason}
              </p>
            )}
          </div>
        </div>
      </div>
      <CardContainer
        cardInfo={planInfo?.payment_information}
        updateURL={planInfo?.management_url}
        isBilling={isBilling}
        actions={subscriptionActions}
      />
    </div>
  );
};

export default CurrentPlan;
